import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { noop } from 'underscore';

import SelfServeDiscoBanner from 'blocks/ProjectsPage/containers/SelfServeDiscoBanner';
import PrivacyPolicyMessage from 'components/PrivacyPolicyMessage';
import { createEmbedExportSelector } from 'containers/ProjectWizards/ExportVideoStep/utils';
import useEddyPromoCheck from 'hooks/useEddyTranscriptionCheck';
import useOnMount from 'hooks/useOnMount';
import { initiatorInfoSelector, videoIdSelector } from 'redux/modules/download';
import { hasRestoreEmbedVideoAccessSelector } from 'redux/modules/pricing';
import ReclipMobileMessage from '../../components/ReclipMobileMessage';
import { block } from '../../utils';
import DownloadPageBannerSlot from '../DownloadPageBannerSlot';
import PromoPopper from '../PromoPopper';
import useDownloadPageState from '../useDownloadPageState';
import DownloadPageArchivingVideoSection from './DownloadPageArchivingVideoSection';
import DownloadPagePreviewSection from './DownloadPagePreviewSection';

export interface DownloadPageProps {
  aspectRatioName?: string;
  eddyProjectUrl?: string;
  canReclip: boolean;
  className?: string;
  loggedIn: boolean;
  onMount?: (
    isAutogramReview: boolean,
    contentInfo: {
      creationRequestId?: number;
      videoId?: number;
    },
  ) => void;
  onUnmount?: () => void;
  videoUrl?: string;
  configurationId?: string;
}

const DownloadPage: React.FC<DownloadPageProps> = ({
  aspectRatioName,
  eddyProjectUrl,
  canReclip,
  className,
  loggedIn,
  onMount,
  onUnmount,
  videoUrl,
  configurationId,
}) => {
  const { isAutogramReview } = useDownloadPageState();
  const initiatorInfo = useSelector(initiatorInfoSelector);
  const videoId = useSelector(videoIdSelector);
  const hasRestoreEmbedVideoAccess = useSelector(
    hasRestoreEmbedVideoAccessSelector,
  );
  const { fileArchiveInfo = {}, id: embedVideoId } = useSelector(
    useMemo(() => createEmbedExportSelector(configurationId), [
      configurationId,
    ]),
  );
  const { downloadPagePopperVisible } = useEddyPromoCheck();

  const { isFileArchived, isRestoreInProgress } = fileArchiveInfo;
  const isVideoArchivedOrRestoring = isFileArchived || isRestoreInProgress;

  useOnMount(
    useCallback(() => {
      onMount(isAutogramReview, {
        creationRequestId: initiatorInfo?.creationRequestId,
        videoId,
      });
    }, [initiatorInfo?.creationRequestId, isAutogramReview, onMount, videoId]),
    onUnmount,
  );

  return (
    <div className={cn(block(), className)}>
      <div
        className={block('container', {
          [aspectRatioName]: !!aspectRatioName,
          archived: isVideoArchivedOrRestoring,
          locked: !hasRestoreEmbedVideoAccess,
        })}
      >
        <DownloadPageBannerSlot />

        {canReclip && <ReclipMobileMessage />}

        {isVideoArchivedOrRestoring ? (
          <DownloadPageArchivingVideoSection
            {...{ embedVideoId, fileArchiveInfo, aspectRatioName }}
          />
        ) : (
          <DownloadPagePreviewSection
            {...{
              aspectRatioName,
              videoUrl,
              canReclip,
            }}
          />
        )}

        {loggedIn && (
          <SelfServeDiscoBanner
            source="DownloadPage"
            className={block('disco-banner', {
              archived: isVideoArchivedOrRestoring,
            })}
          />
        )}
      </div>

      <PrivacyPolicyMessage />

      {downloadPagePopperVisible && (
        <PromoPopper eddyProjectUrl={eddyProjectUrl} />
      )}
    </div>
  );
};

DownloadPage.defaultProps = {
  onMount: noop,
};

export default DownloadPage;
